import "utils/initializeMomentGlobal";
import { useEffect, useRef } from "react";
import moment from "moment";
import omit from "lodash/omit";
import { getLocaleString } from "utils/localization/locale";
import DateRangePicker from "react-bootstrap-daterangepicker";
import TokenManager from "utils/TokenManager";
const defaultLabel = "MM/DD/YYYY";

const ALL_DATE_RANGE = [moment(0), moment()];
const ALL_DATE_RANGE_TILL_TODAY = [moment(0), moment().add(1, "year")];

export let ranges = {
  [getLocaleString("common.dateRangePicker.all")]: ALL_DATE_RANGE,
  AllToDate: ALL_DATE_RANGE_TILL_TODAY,
  [getLocaleString("common.dateRangePicker.today")]: [moment(), moment()],
  [getLocaleString("common.dateRangePicker.yesterday")]: [
    moment().subtract(1, "days"),
    moment().subtract(1, "days"),
  ],
  [getLocaleString("common.dateRangePicker.next7Days")]: [
    moment(),
    moment().add(7, "days"),
  ],
  [getLocaleString("common.dateRangePicker.next30Days")]: [
    moment(),
    moment().add(30, "days"),
  ],
  [getLocaleString("common.dateRangePicker.past7Days")]: [
    moment().subtract(7, "days"),
    moment(),
  ],
  [getLocaleString("common.dateRangePicker.past30Days")]: [
    moment().subtract(30, "days"),
    moment(),
  ],
  [getLocaleString("common.dateRangePicker.past90Days")]: [
    moment().subtract(90, "days"),
    moment(),
  ],
  [getLocaleString("common.dateRangePicker.past365Days")]: [
    moment().subtract(365, "days"),
    moment(),
  ],
};

const excludeInReceivables = {
  Today: [moment(), moment()],
  Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
  [getLocaleString("common.dateRangePicker.next7Days")]: [
    moment(),
    moment().add(7, "days"),
  ],
  [getLocaleString("common.dateRangePicker.past7Days")]: [
    moment().subtract(7, "days"),
    moment(),
  ],
};

const defaultAgingBucketRanges = {
  [getLocaleString("common.dateRangePicker.past_1_30")]: [
    moment().subtract(30, "days"),
    moment().subtract(1, "days"),
  ],
  [getLocaleString("common.dateRangePicker.past_31_60")]: [
    moment().subtract(60, "days"),
    moment().subtract(31, "days"),
  ],
  [getLocaleString("common.dateRangePicker.past_61_90")]: [
    moment().subtract(90, "days"),
    moment().subtract(61, "days"),
  ],
  [getLocaleString("common.dateRangePicker.past_gt_90")]: [
    moment().subtract(2, "years"),
    moment().subtract(91, "days"),
  ],
};

const rangeSequence = [
  getLocaleString("common.dateRangePicker.all"),
  getLocaleString("common.dateRangePicker.today"),
  getLocaleString("common.dateRangePicker.yesterday"),
  getLocaleString("common.dateRangePicker.next7Days"),
  getLocaleString("common.dateRangePicker.next30Days"),
  getLocaleString("common.dateRangePicker.past7Days"),
  getLocaleString("common.dateRangePicker.past30Days"),
  getLocaleString("common.dateRangePicker.past90Days"),
  getLocaleString("common.dateRangePicker.past365Days"),
  getLocaleString("common.dateRangePicker.past_gt_30"),
  getLocaleString("common.dateRangePicker.past_gt_60"),
];

function CustomDateRangePicker(props) {
  const {
    all,
    label,
    opens = "right",
    endDate,
    classes,
    maxSpan,
    parentEl,
    allToDate,
    startDate,
    isClearable,
    isFilterChip = false,
    showAgingBuckets = false,
    getSelectedDates,
    agingBucketRanges = defaultAgingBucketRanges,
  } = props;

  const agingBucketIntervals = Object.keys(agingBucketRanges);

  const datePickerRef = useRef();
  let updatedStartDate = startDate
    ? moment(startDate).set({ hour: 0, minute: 0, second: 0 })
    : undefined;
  let updatedEndDate = endDate
    ? moment(endDate).set({ hour: 23, minute: 59, second: 59 })
    : undefined;

  if (label && label !== "Custom Range" && ranges[label]) {
    [updatedStartDate, updatedEndDate] = ranges[label];
  }

  // Update picker when props change
  useEffect(() => {
    if (datePickerRef.current && startDate && endDate) {
      const newStartDate = moment(startDate).set({
        hour: 0,
        minute: 0,
        second: 0,
      });
      const newEndDate = moment(endDate).set({
        hour: 23,
        minute: 59,
        second: 59,
      });

      datePickerRef.current.setStartDate(newStartDate);
      datePickerRef.current.setEndDate(newEndDate);
    }
  }, [startDate, endDate]);

  const getDates = (picker) => {
    if (picker) {
      updatedStartDate = picker.startDate;
      updatedEndDate = picker.endDate;
      if (datePickerRef.current) {
        datePickerRef.current.setStartDate(updatedStartDate);
        datePickerRef.current.setEndDate(updatedEndDate);
      }
      getSelectedDates(updatedStartDate, updatedEndDate, picker.chosenLabel);
      handleAllRangeRender(
        picker.chosenLabel,
        updatedStartDate,
        updatedEndDate
      );
    }
  };

  const handleAllRangeRender = (label, startDate, endDate) => {
    const allLabels = [
      getLocaleString("common.dateRangePicker.all"),
      "All",
      "AllToDate",
    ];

    // Ensure startDate and endDate are moment objects
    const momentStart = moment.isMoment(startDate)
      ? startDate
      : moment(startDate);
    const momentEnd = moment.isMoment(endDate) ? endDate : moment(endDate);

    // Check if dates match the "All" range using constants
    const isAllRange =
      momentStart &&
      momentEnd &&
      momentStart.isValid() &&
      momentEnd.isValid() &&
      // Check for ALL_DATE_RANGE: moment(0) to current moment
      ((momentStart.isSame(moment(0), "day") &&
        momentEnd.isSame(moment(), "day")) ||
        // Check for ALL_DATE_RANGE_TILL_TODAY: moment(0) to moment().add(1, 'year')
        (momentStart.isSame(moment(0), "day") &&
          momentEnd.isSame(moment().add(1, "year"), "day")));

    if (
      (allLabels.includes(label) || isAllRange) &&
      datePickerRef.current?.ref
    ) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        if (datePickerRef.current?.ref) {
          datePickerRef.current.ref.value = "All";
        }
      }, 0);
    }
  };
  const currentDateFormat = TokenManager.getDateFormat();

  if (allToDate) {
    ranges["All"] = ALL_DATE_RANGE_TILL_TODAY;
  } else if (all) {
    ranges["All"] = ALL_DATE_RANGE;
  }
  if (!all && !allToDate) {
    ranges = omit(ranges, "All");
  }
  if (showAgingBuckets) {
    ranges = omit(ranges, [
      "Today",
      "Yesterday",
      getLocaleString("common.dateRangePicker.next7Days"),
      getLocaleString("common.dateRangePicker.past7Days"),
    ]);
    ranges = {
      ...ranges,
      ...agingBucketRanges,
      [getLocaleString("common.dateRangePicker.past_gt_30")]: [
        moment(0),
        moment().subtract(30, "days"),
      ],
      [getLocaleString("common.dateRangePicker.past_gt_60")]: [
        moment(0),
        moment().subtract(60, "days"),
      ],
    };
  } else {
    ranges = omit(ranges, [
      ...agingBucketIntervals,
      getLocaleString("common.dateRangePicker.past_gt_30"),
      getLocaleString("common.dateRangePicker.past_gt_60"),
    ]);
    ranges = {
      ...ranges,
      ...excludeInReceivables,
    };
  }

  const mappedRange = rangeSequence
    .concat(showAgingBuckets ? agingBucketIntervals : [])
    .reduce((acc, cur) => {
      if (cur && ranges[cur]) {
        return {
          ...acc,
          [cur]: ranges[cur],
        };
      }
      return acc;
    }, {});

  // Consolidated effect for label updates
  useEffect(() => {
    if (datePickerRef.current?.ref) {
      if (!updatedStartDate) {
        datePickerRef.current.ref.value = "";
      } else {
        handleAllRangeRender(label, updatedStartDate, updatedEndDate);
      }
    }
  }, [updatedStartDate, updatedEndDate, label]);

  const handleHide = (event, picker) => {
    const { chosenLabel, startDate, endDate } = picker;
    // Delay to ensure the input value is set after picker closes
    setTimeout(() => {
      handleAllRangeRender(chosenLabel, startDate, endDate);
    }, 10);
  };

  return (
    <div id="datepicker-wrap" className="min-width-200">
      <div id="wrap-date" className="rounded ">
        <i
          id="fa-calender-real "
          className="fa fa-calendar real text-primary"
        />
        &nbsp;
        <DateRangePicker
          ref={datePickerRef}
          initialSettings={{
            parentEl,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
            ranges: mappedRange,
            className: classes,
            width: "100",
            showDropdowns: true,
            maxSpan: maxSpan,
            opens: opens,
            linkedCalendars: false,
            locale: {
              customRangeLabel: getLocaleString(
                "common.dateRangePicker.custom",
                "Custom Range"
              ),
              applyLabel: getLocaleString("common.apply", "Apply"),
              cancelLabel: getLocaleString("common.cancel", "Cancel"),
              format: currentDateFormat || defaultLabel,
            },
            format: currentDateFormat || defaultLabel,
          }}
          onApply={(_, picker) => getDates(picker)}
          onCallback={(startDate, endDate, chosenLabel) =>
            getDates({ startDate, endDate, chosenLabel })
          }
          onHide={handleHide}
        >
          <input
            type="text"
            placeholder={`${currentDateFormat} - ${currentDateFormat}`}
            className="border-none outline-none w-90 d-inline-block t-12"
          />
        </DateRangePicker>
        {isClearable && (
          <i
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              getDates({
                startDate: undefined,
                endDate: undefined,
                chosenLabel: "",
              });
              datePickerRef.current.ref.value = "";
              datePickerRef.current.ref.focus();
            }}
            id="calender-fa-cross"
            className="fas fa-times float-end text-primary"
            style={{
              position: "absolute",
              top: 12,
              marginRight: isFilterChip ? "10px" : "",
            }}
          />
        )}
      </div>
    </div>
  );
}

export default CustomDateRangePicker;
